import { useEffect, useState } from "react";
import clsx from "clsx";
import { Spinner } from "@/components/ui/Spinner";
import { isIdentNumberQuery, isSearchQueryReady } from "@/lib/searchQuery";

export interface SearchInputProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  debounceMs?: number;
  className?: string;
  /** True, solange die Suche im Backend läuft (Spinner im Feld). */
  loading?: boolean;
  /**
   * Prädikat für Sofortsuche ohne Debounce (Default: Projekt- und Belegnummern).
   * Die Artikelliste übergibt hier die Artikel-/EAN-Erkennung, damit
   * Barcode-Scanner-Eingaben sofort suchen.
   */
  immediateQuery?: (query: string) => boolean;
}

/**
 * Suchfeld mit Debounce. Tippen aktualisiert nur den lokalen State; nach
 * `debounceMs` (default 300 ms) ohne weitere Eingabe wird `onChange` mit dem
 * neuen Wert aufgerufen, und damit das tatsächliche Refetch ausgelöst.
 *
 * Ausnahmen (passend zur hybriden Backend-Suche):
 * - Nummern-Anfragen (`immediateQuery`, Default: Projekt-/Ordnernummern)
 *   suchen sofort ohne Debounce – auch Barcode-Scanner-Eingaben.
 * - Enter löst die Suche sofort aus (Scanner senden meist Enter am Ende).
 * - Freitext unter der Mindestlänge (2 Zeichen) löst keine Suche aus.
 * - Leeren des Felds setzt den Filter sofort zurück.
 */
export function SearchInput({
  value,
  onChange,
  placeholder = "Suchen …",
  debounceMs = 300,
  className,
  loading = false,
  immediateQuery,
}: SearchInputProps) {
  const [draft, setDraft] = useState(value);
  const pendingSearch = draft !== value && isSearchQueryReady(draft);
  const busy = pendingSearch || (loading && Boolean(draft.trim()));

  // Externe Änderungen am Wert (z. B. Reset) nach unten propagieren.
  useEffect(() => {
    setDraft(value);
  }, [value]);

  // Debounce nach jeder Tastatureingabe.
  useEffect(() => {
    if (draft === value) return;
    if (!isSearchQueryReady(draft)) return;
    const trimmed = draft.trim();
    const sofort = trimmed === "" || (immediateQuery ?? isIdentNumberQuery)(trimmed);
    const handle = window.setTimeout(
      () => {
        onChange(draft);
      },
      sofort ? 0 : debounceMs,
    );
    return () => window.clearTimeout(handle);
  }, [draft, value, debounceMs, onChange, immediateQuery]);

  return (
    <div className={clsx("relative", className)}>
      <span
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-label-tertiary"
      >
        {busy ? <Spinner className="h-[18px] w-[18px]" /> : <SearchIcon />}
      </span>
      <input
        type="text"
        inputMode="search"
        enterKeyHint="search"
        autoComplete="off"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          // Enter = sofort suchen (Barcode-Scanner senden Enter am Ende).
          if (e.key === "Enter" && isSearchQueryReady(draft) && draft !== value) {
            e.preventDefault();
            onChange(draft);
          }
        }}
        placeholder={placeholder}
        className="eh-input pl-9 pr-9"
        aria-label={placeholder}
        aria-busy={busy || undefined}
      />
      {draft && (
        <button
          type="button"
          onClick={() => {
            setDraft("");
            onChange("");
          }}
          className="absolute inset-y-0 right-2 flex items-center justify-center px-1 text-label-tertiary transition hover:text-label"
          aria-label="Suche leeren"
        >
          <ClearIcon />
        </button>
      )}
    </div>
  );
}

function SearchIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <circle cx="11" cy="11" r="7" />
      <line x1="20" y1="20" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}
